import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Sun, Moon, ArrowUpRight, Radar } from "lucide-react";

const links = [
  { id: "about", label: "About", n: "01" },
  { id: "skills", label: "Skills", n: "02" },
  { id: "certifications", label: "Certifications", n: "03" },
  { id: "experience", label: "Experience", n: "04" },
  { id: "projects", label: "Work", n: "05" },
  { id: "intelligence", label: "Tech Intelligence", n: "06", route: "/intelligence" },
  { id: "contact", label: "Contact", n: "07" },
];

export default function Nav({ theme, toggleTheme }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onIntelPage = location.pathname !== "/";
  const [active, setActive] = useState(onIntelPage ? "intelligence" : "hero");
  const [scrolled, setScrolled] = useState(false);
  const ticking = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 40);
          if (!onIntelPage) {
            const y = window.scrollY + window.innerHeight * 0.35;
            const ids = ["hero", ...links.map((l) => l.id)];
            let current = "hero";
            for (const id of ids) {
              const el = document.getElementById(id);
              if (el && el.offsetTop <= y) current = id;
            }
            setActive(current);
          }
          ticking.current = false;
        });
        ticking.current = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [onIntelPage]);

  const scrollTo = (id) => {
    if (onIntelPage) {
      navigate("/", { state: { scrollTo: id === "hero" ? null : id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // On the homepage every item scroll-spies to its section; off the homepage, route links open their page.
  const go = (l) => (l.route && onIntelPage ? navigate(l.route) : scrollTo(l.id));

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-background/70 border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          data-testid="nav-brand"
          className="group flex items-center gap-3"
        >
          <span className="w-2 h-2 rounded-full bg-accent shadow-[0_0_12px_hsl(var(--accent))]" />
          <span className="font-heading font-black tracking-tight text-sm md:text-base uppercase">
            Madan<span className="text-accent">.</span>Aritakula
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-0.5">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l)}
              className="group relative px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] whitespace-nowrap"
            >
              <span className="text-muted-foreground mr-1">{l.n}</span>
              <span
                className={`transition-colors ${
                  active === l.id ? "text-accent" : "text-foreground group-hover:text-accent"
                }`}
              >
                {l.label}
              </span>
              {active === l.id && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute left-3 right-3 -bottom-0.5 h-px bg-accent"
                />
              )}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-link-intelligence-mobile"
            aria-label="Tech Intelligence"
            onClick={() => navigate("/intelligence")}
            className={`lg:hidden w-10 h-10 grid place-items-center border rounded-full transition-colors ${
              onIntelPage ? "border-accent text-accent" : "border-border hover:border-accent hover:text-accent"
            }`}
          >
            <Radar size={16} />
          </button>
          <button
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            onClick={toggleTheme}
            className="w-10 h-10 grid place-items-center border border-border rounded-full hover:border-accent hover:text-accent transition-colors"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <a
            data-testid="nav-cta-contact"
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              scrollTo("contact");
            }}
            className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] bg-accent text-accent-foreground px-4 py-2.5 hover:bg-foreground hover:text-background transition-colors"
          >
            Let’s talk <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </motion.header>
  );
}
